'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  FieldWrapper,
  Form,
  FormField,
  PhoneInput,
  TextInput,
  TextArea,
  Icon,
  useToast,
} from '@polyseme/design-system';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '~/landing/components/shared';
import { api } from '~/landing/trpc/client';

import { Tabs, TabsList, TabsTrigger } from '../components/tabs';
import { Title } from '../components/title';
import { Team } from './team';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const contactFormSchema = z.object({
  firstname: z.string().min(1, 'Prénom requis'),
  lastname: z.string().min(1, 'Nom requis'),
  email: z.string().email('Email invalide'),
  phoneNumber: z.string().min(1, 'Téléphone requis'),
  company: z.string().min(1, 'Entreprise requise'),
  message: z.string().optional(),
});

type ContactForm = z.infer<typeof contactFormSchema>;
export type RequestType = 'demo' | 'integration' | 'simulator';

export const ContactSection = ({
  hasTabs = false,
  landingType = 'polyseme',
  defaultRequestType = 'demo',
}: {
  hasTabs?: boolean;
  landingType?: 'germen' | 'polyseme';
  defaultRequestType?: RequestType;
}) => {
  const t = useTranslations(`landing.${landingType}.contact`);
  const tRequest = useTranslations(`landing.common.contactEmail`);

  const { toast } = useToast();
  const [requestType, setRequestType] =
    useState<RequestType>(defaultRequestType);

  const form = useForm<ContactForm>({
    resolver: zodResolver(contactFormSchema),
  });

  const sendContactEmail = api.email.sendContactEmail.useMutation({
    onSuccess: () => {
      toast({
        title: t('success.title'),
        description: t('success.description'),
      });
      form.reset();
    },
  });

  const onSubmit = (data: ContactForm) => {
    sendContactEmail.mutate({
      ...data,
      requestType: tRequest(`requestType.${requestType}`),
      app: tRequest(`app.${landingType}`),
    });
  };

  return (
    <section
      className="mx-auto flex w-full max-w-screen-xl flex-col px-4 py-[60px]"
      id="contact"
    >
      <motion.div
        className="mb-10 text-center"
        initial="hidden"
        transition={{ duration: 0.5, ease: 'easeOut' }}
        variants={fadeInUp}
        viewport={{ once: true, amount: 0.3 }}
        whileInView="visible"
      >
        <Title as="h2" variant="title-2">
          {t('title')}
        </Title>
      </motion.div>

      <motion.div
        className="mx-auto w-full max-w-2xl pb-[60px]"
        initial="hidden"
        transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
        variants={fadeInUp}
        viewport={{ once: true, amount: 0.2 }}
        whileInView="visible"
      >
        {!!hasTabs && (
          <Tabs
            className="mb-8"
            defaultValue="demo"
            value={requestType}
            onValueChange={(value) => setRequestType(value as RequestType)}
          >
            <TabsList>
              <TabsTrigger value="demo">{t('tabs.demo')}</TabsTrigger>
              <TabsTrigger value="integration">
                {t('tabs.integration')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 gap-6 px-10 md:grid-cols-2 md:px-0">
              <FormField
                control={form.control}
                name="firstname"
                render={({ field }) => (
                  <FieldWrapper required label={t('form.firstname')}>
                    <TextInput autoComplete="given-name" {...field} />
                  </FieldWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="lastname"
                render={({ field }) => (
                  <FieldWrapper required label={t('form.lastname')}>
                    <TextInput autoComplete="family-name" {...field} />
                  </FieldWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FieldWrapper required label={t('form.email')}>
                    <TextInput autoComplete="email" type="email" {...field} />
                  </FieldWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FieldWrapper required label={t('form.phoneNumber')}>
                    <PhoneInput {...field} />
                  </FieldWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FieldWrapper
                    required
                    className="md:col-span-2"
                    label={t('form.company')}
                  >
                    <TextInput autoComplete="organization" {...field} />
                  </FieldWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FieldWrapper
                    className="md:col-span-2"
                    label={t('form.message')}
                  >
                    <TextArea rows={5} {...field} />
                  </FieldWrapper>
                )}
              />
            </div>

            <div className="mt-8 flex justify-center">
              <Button
                className="w-full p-6 md:w-auto"
                disabled={sendContactEmail.isPending}
                size="xl"
                type="submit"
              >
                {sendContactEmail.isPending ? t('sending') : t('submit')}{' '}
                <Icon name="circle-play" />
              </Button>
            </div>
          </form>
        </Form>
      </motion.div>
      <Team />
    </section>
  );
};
